export const TEAM_COLOR_CONFIG = {
  minimumSaturation: 0.22,
  minimumValue: 0.14,
  minimumChroma: 24,
  minimumRowConfidence: 0.18,
  blue: {
    hueMin: 185,
    hueMax: 250,
    minChannel: 90,
    minDominance: 18,
  },
  red: {
    hueMin: 335,
    hueMax: 24,
    minChannel: 90,
    minDominance: 18,
  },
};

export const rgbToHsv = ({ r, g, b }) => {
  const red = r / 255;
  const green = g / 255;
  const blue = b / 255;
  const max = Math.max(red, green, blue);
  const min = Math.min(red, green, blue);
  const delta = max - min;
  let h = 0;

  if (delta !== 0) {
    if (max === red) h = 60 * (((green - blue) / delta) % 6);
    if (max === green) h = 60 * ((blue - red) / delta + 2);
    if (max === blue) h = 60 * ((red - green) / delta + 4);
  }

  return {
    h: h < 0 ? h + 360 : h,
    s: max === 0 ? 0 : delta / max,
    v: max,
  };
};

export const colorDistance = (a, b) => {
  const r = (a.r || 0) - (b.r || 0);
  const g = (a.g || 0) - (b.g || 0);
  const blue = (a.b || 0) - (b.b || 0);
  return Math.sqrt((r * r) + (g * g) + (blue * blue));
};

export const averageColors = (colors) => {
  if (!colors.length) return { r: 0, g: 0, b: 0 };

  const total = colors.reduce((acc, color) => ({
    r: acc.r + color.r,
    g: acc.g + color.g,
    b: acc.b + color.b,
  }), { r: 0, g: 0, b: 0 });

  return {
    r: Math.round(total.r / colors.length),
    g: Math.round(total.g / colors.length),
    b: Math.round(total.b / colors.length),
  };
};

export const classifyPixelTeamColor = (color) => {
  const hsv = rgbToHsv(color);
  const chroma = Math.max(color.r, color.g, color.b) - Math.min(color.r, color.g, color.b);
  const isStrongEnough = (
    hsv.s >= TEAM_COLOR_CONFIG.minimumSaturation &&
    hsv.v >= TEAM_COLOR_CONFIG.minimumValue &&
    chroma >= TEAM_COLOR_CONFIG.minimumChroma
  );

  if (!isStrongEnough) {
    return 'unknown';
  }

  const isBlueHue = hsv.h >= TEAM_COLOR_CONFIG.blue.hueMin && hsv.h <= TEAM_COLOR_CONFIG.blue.hueMax;
  const isRedHue = hsv.h <= TEAM_COLOR_CONFIG.red.hueMax || hsv.h >= TEAM_COLOR_CONFIG.red.hueMin;
  const blueDominance = color.b - Math.max(color.r, color.g);
  const redDominance = color.r - Math.max(color.g, color.b);

  if (isBlueHue || (
    color.b > TEAM_COLOR_CONFIG.blue.minChannel &&
    blueDominance > TEAM_COLOR_CONFIG.blue.minDominance
  )) {
    return 'blue';
  }

  if (isRedHue || (
    color.r > TEAM_COLOR_CONFIG.red.minChannel &&
    redDominance > TEAM_COLOR_CONFIG.red.minDominance
  )) {
    return 'red';
  }

  return 'unknown';
};

export const classifyColorSamples = (samples) => {
  const votes = samples.reduce((acc, sample) => {
    const teamColor = classifyPixelTeamColor(sample);
    acc[teamColor] += 1;
    return acc;
  }, { blue: 0, red: 0, unknown: 0 });

  const teamVotes = votes.blue + votes.red;
  const winner = votes.blue > votes.red ? 'blue' : votes.red > votes.blue ? 'red' : 'unknown';
  const confidence = teamVotes
    ? Math.max(votes.blue, votes.red) / Math.max(1, samples.length)
    : 0;

  return {
    teamColor: confidence >= TEAM_COLOR_CONFIG.minimumRowConfidence && winner !== 'unknown' ? winner : 'unknown',
    confidence,
    votes,
    averageColor: averageColors(samples),
    averageHsv: rgbToHsv(averageColors(samples)),
    sampleCount: samples.length,
  };
};
